import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Lock, Phone } from 'lucide-react';
import { prettyPhone } from '@/lib/phone';

function Logo({ inverted = false }: { inverted?: boolean }) {
  return (
    <Link to="/feed" className="inline-flex items-center">
      <img
        src={inverted ? '/brand/fconnect-logo-horizontal-blanc.svg' : '/brand/fconnect-logo-horizontal.svg'}
        alt="FCONNECT"
        className="h-10 w-auto"
      />
    </Link>
  );
}

/** Les lignes d'un terrain, en filigrane du panneau de marque. */
function PitchLines() {
  return (
    <svg
      className="absolute inset-0 h-full w-full text-white/10"
      viewBox="0 0 400 600"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="20" y="20" width="360" height="560" />
      <line x1="20" y1="300" x2="380" y2="300" />
      <circle cx="200" cy="300" r="60" />
      <circle cx="200" cy="300" r="3" fill="currentColor" />
      <rect x="100" y="20" width="200" height="90" />
      <rect x="150" y="20" width="100" height="35" />
      <rect x="100" y="490" width="200" height="90" />
      <rect x="150" y="545" width="100" height="35" />
    </svg>
  );
}

interface AuthLayoutProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Cadre commun de la connexion et de l'inscription.
 *
 * Sur grand écran, le panneau vert porte la marque et laisse au formulaire
 * toute la colonne de droite. Sur mobile, il disparaît : le formulaire doit
 * tenir au-dessus du clavier, et le logo suffit à dire où l'on est.
 */
export function AuthLayout({ title, subtitle, children, footer }: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen bg-white">
      <aside className="relative hidden w-[44%] max-w-xl flex-col justify-between overflow-hidden bg-primary-700 p-10 lg:flex">
        <PitchLines />
        <div className="relative">
          <Logo inverted />
        </div>

        <div className="relative">
          <h2 className="text-4xl font-bold leading-tight text-white">
            Le football sénégalais,{' '}
            {/* Le jaune se pose sous l'encre, jamais en texte : voir tailwind.config. */}
            <span className="bg-accent px-1.5 text-gray-900 [box-decoration-break:clone]">
              au même endroit.
            </span>
          </h2>
          <ul className="mt-8 space-y-3 text-primary-50">
            <li className="flex items-start gap-3">
              <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-accent" />
              Suivez votre club, ses matchs et ses joueurs en direct.
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-accent" />
              Joueurs et entraîneurs : montrez votre parcours aux recruteurs.
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-accent" />
              Un numéro de téléphone et un code suffisent pour entrer.
            </li>
          </ul>
        </div>

        <p className="relative text-sm text-primary-200">© {new Date().getFullYear()} FCONNECT</p>
      </aside>

      <main className="flex flex-1 flex-col px-4 py-8 sm:px-8">
        <div className="lg:hidden">
          <Logo />
        </div>

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-8">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{title}</h1>
          {subtitle && <p className="mt-2 text-gray-600">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 border-t border-gray-200 pt-6">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  autoComplete?: string;
  autoFocus?: boolean;
}

/** Numéro de téléphone, aéré pendant la frappe. */
export function PhoneField({ id, label, value, onChange, hint, autoFocus }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      <div className="relative">
        <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          id={id}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          autoFocus={autoFocus}
          required
          value={prettyPhone(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder="77 123 45 67"
          className="input pl-9 tracking-wide"
        />
      </div>
      {hint && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

/** Code ou mot de passe, avec un œil pour vérifier ce qu'on a tapé. */
export function SecretField({
  id,
  label,
  value,
  onChange,
  hint,
  autoComplete = 'current-password',
  placeholder = '••••••••',
  aside,
}: FieldProps & { placeholder?: string; aside?: ReactNode }) {
  const [visible, setVisible] = useState(false);

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label htmlFor={id} className="label">
          {label}
        </label>
        {aside}
      </div>
      <div className="relative">
        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="input px-9"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700"
          aria-label={visible ? 'Masquer' : 'Afficher'}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {hint && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
